import React from 'react';
import PropTypes from 'prop-types';
import { compose, defaultProps, withProps } from 'recompose';
import { includes, isString, upperFirst, toUpper, isEmpty, get } from 'lodash';

import { FAQ } from 'components/core/FAQ';
import { Section } from 'components/core/Page';
import ReviewCardList from 'components/review/ReviewCard/ReviewCardList';
import { DrillDownLinks } from 'components/content/DrillDownLinks';
import { MarkdownWrapper } from 'components/search/MarkdownWrapper';
import BlogPosts from './BlogPosts';
import StaticRVBlogData from './StaticRVBlogData.json';
import PricingStats from './PricingStats';

import './SearchContentVariation.scss';
import { toTitleCase } from '../../../utils/format';

export const enhance = compose(
	defaultProps({ searchParams: {}, location: {} }),
	withProps(({ location, searchParams }) => {
		let formattedVehicleName = '';
		if (isString(searchParams.vehicle) && searchParams.vehicle.length > 0) {
			formattedVehicleName =
				searchParams.vehicle.length > 2
					? upperFirst(searchParams.vehicle) // Car, Boat
					: toUpper(searchParams.vehicle); // RV
		}
		return {
			city: location.city,
			neighborhood: location.subLocality || location.city,
			state: location.fullState,
			stateAbbreviation: get(location, 'state', ''),
			vehicleType: searchParams.vehicle,
			vehicleName: formattedVehicleName,
			isRVStorage: searchParams.vehicle === 'rv',
			isVehicleStorage: includes(
				['car', 'rv', 'boat'],
				searchParams.vehicle,
			),
			isCarStorage: searchParams.vehicle === 'car',
			isCLP: get(searchParams, 'subPageType', '') === 'cityLandingPage',
		};
	}),
);

export function SearchContentVariation({
	state,
	city,
	vehicleType,
	vehicleName,
	isVehicleStorage,
	content,
	isCLP,
	faqShowing,
	faqContent,
	amenity,
	location,
	stateAbbreviation,
	isNearMePage,
}) {
	const hasNearbyCities = !isEmpty(content.nearbyCities);
	const hasTpReviews = !!content?.tpReviews?.length;
	const nearbyCityLinks = hasNearbyCities
		? content.nearbyCities.map((nearbyCity) => ({
				text: `${nearbyCity.city} Storage Units`,
				href: nearbyCity.url,
			}))
		: [];

	// maps available amenity slugs to plain text for FAQ header
	const plainAmenityText =
		amenity == 'climate-controlled'
			? 'Climate Controlled'
			: amenity == '24-hour-access'
				? '24 Hour'
				: amenity == 'drive-up-storage-units'
					? 'Drive Up'
					: '';

	const faqHeaderText = toTitleCase(vehicleType) || plainAmenityText || '';

	const faqHeader = `Frequently asked questions about ${faqHeaderText.toLowerCase()} storage in ${city}, ${state}`;

	if (isNearMePage) {
		return (
			<div className="search-content-variation">
				{hasTpReviews && (
					<Section className="search-reviews-section both-borders">
						<ReviewCardList
							reviews={content.tpReviews}
							isNearMePage={isNearMePage}
						/>
					</Section>
				)}
			</div>
		);
	}

	return (
		<div className="search-content-variation">
			{!isEmpty(content.pricingStats) && (
				<Section className="pricing-stats-section">
					<PricingStats
						storageType={content.formattedStorageType}
						city={city}
						stateAbbreviation={location.state}
						isVehicleStorage={isVehicleStorage}
						pricingStats={content.pricingStats}
						isCLP={isCLP}
						isSearchPageVariation={true}
						locationName={`${city}, ${state}`}
					/>
				</Section>
			)}
			{hasTpReviews && (
				<Section className="search-reviews-section both-borders">
					<ReviewCardList reviews={content.tpReviews} />
				</Section>
			)}

			{content?.seoBodyJson && (
				<Section className="markdown-wrapper">
					<MarkdownWrapper content={content.seoBodyJson} />
				</Section>
			)}

			{faqShowing && (
				<Section className="bottom-border">
					<FAQ
						content={faqContent}
						header={faqHeader}
					/>
				</Section>
			)}

			{(!!content.drillDownLinks || hasNearbyCities) && (
				<Section className="drill-down-links-section">
					{!!content.drillDownLinks &&
						!!content.drillDownLinks.amenities && (
							<DrillDownLinks
								headline={`Browse ${city} Storage by Amenity`}
								links={content.drillDownLinks.amenities}
							/>
						)}
					{!!content.drillDownLinks &&
						!!content.drillDownLinks.vehicleStorage && (
							<DrillDownLinks
								headline={`Browse ${city} Storage by Type`}
								links={content.drillDownLinks.vehicleStorage}
							/>
						)}

					{!!content.drillDownLinks &&
						!!content.drillDownLinks.unitTypes && (
							<DrillDownLinks
								headline={`Browse ${city} Storage by Unit Size`}
								links={content.drillDownLinks.unitTypes}
							/>
						)}
					{hasNearbyCities && (
						<DrillDownLinks
							headline="Storage in nearby cities"
							links={nearbyCityLinks}
						/>
					)}
				</Section>
			)}

			{!!isVehicleStorage && (
				<Section className="top-border">
					<BlogPosts
						headerText={`Helpful resources for storage in ${city}, ${stateAbbreviation}`}
						posts={StaticRVBlogData[vehicleType]}
					/>
				</Section>
			)}
			{isCLP && !isEmpty(content.movingGuidePosts) && (
				<Section className="top-border">
					<BlogPosts
						headerText={`Helpful resources for storage in ${city}, ${stateAbbreviation}`}
						posts={content.movingGuidePosts}
					/>
				</Section>
			)}
			{isCLP &&
				!isEmpty(content.blogPosts) &&
				isEmpty(content.movingGuidePosts) && (
					<Section className="top-border">
						<BlogPosts
							headerText={`Helpful resources for storage in ${city}, ${stateAbbreviation}`}
							posts={content.blogPosts}
						/>
					</Section>
				)}
		</div>
	);
}

SearchContentVariation.propTypes = {
	state: PropTypes.string,
	city: PropTypes.string,
	vehicleType: PropTypes.string,
	isVehicleStorage: PropTypes.bool,
	vehicleName: PropTypes.string,
	stateAbbreviation: PropTypes.string,
	location: PropTypes.shape({
		city: PropTypes.string,
		state: PropTypes.string,
	}),
	content: PropTypes.shape({
		drillDownLinks: PropTypes.object,
		nearbyCities: PropTypes.array,
		blogPosts: PropTypes.array,
		movingGuidePosts: PropTypes.array,
		pricingStats: PropTypes.object,
		formattedStorageType: PropTypes.string,
		lowPerformingAlt: PropTypes.shape({
			alternateState: PropTypes.string,
			alternateCity: PropTypes.string,
		}),
		averageMonthlyUnitPrice: PropTypes.object,
	}),
	isCLP: PropTypes.bool,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	amenity: PropTypes.string,
	isNearMePage: PropTypes.bool,
};

SearchContentVariation.defaultProps = {
	content: {},
};

export default enhance(SearchContentVariation);
